/* @HEADER
 * @version 0.16.496 | 2026-08-10
 * @purpose Emoji + color picker popover for project icons in the project switcher.
 * @sidecar EmojiPicker.tsx.header.md
 * @layer app | @hex adapter | @ctx project
 * @public false
 * @edit careful
 */

import { useRef, useEffect, useCallback, useState } from 'react'
import { useMessages } from '../shared/i18n/useMessages'
import { InlineNotice } from '../ui/InlineNotice'
import { EMOJI_PICKER_TESTIDS } from './emoji-picker.testids'

const EMOJI_LIST = [
  '\u{1F680}', '\u{1F4BC}', '\u{1F52C}', '\u{1F3AF}', '\u{1F3E0}', '\u{1F4CA}', '\u{1F3A8}', '\u{1F527}',
  '\u{1F4DD}', '\u{1F4A1}', '\u{1F31F}', '\u{1F3C6}', '\u{1F3AE}', '\u{1F3B5}', '\u{1F4DA}', '\u{1F30D}',
  '\u{26A1}', '\u{1F525}', '\u{1F48E}', '\u{1F6E0}', '\u{1F4E6}', '\u{1F3AA}', '\u{1F308}', '\u{1F340}',
  '\u{1F98A}', '\u{1F41D}', '\u{1F98B}', '\u{1F419}', '\u{1F33B}', '\u{1F34E}', '\u{2615}', '\u{1F3B2}',
  '\u{1F3D7}', '\u{1F511}', '\u{1F381}', '\u{1F9E9}', '\u{1F5FA}', '\u{1F4B0}', '\u{1F393}', '\u{1F9EA}',
]

const COLOR_PRESETS = [
  '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899',
  '#ef4444', '#f59e0b', '#10b981', '#6b7280',
]

interface EmojiPickerProps {
  value?: string
  color?: string
  onSelect: (emoji: string, color: string) => void
  onClose: () => void
}

export function EmojiPicker({ value, color, onSelect, onClose }: EmojiPickerProps) {
  const m = useMessages()
  const ref = useRef<HTMLDivElement>(null)
  const currentColor = color || '#3b82f6'
  // ZVX-DEV-3630: replaces the native alert() that used to report an
  // oversized upload — non-blocking, dismissible, local to this popover.
  const [uploadError, setUploadError] = useState<string | null>(null)

  const handleUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 64 * 1024) {
      setUploadError(m.emojiPicker.uploadTooLarge)
      return
    }
    const canvas = document.createElement('canvas')
    canvas.width = 56
    canvas.height = 56
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 56, 56)
      const dataUrl = canvas.toDataURL('image/png')
      onSelect(dataUrl, currentColor)
    }
    img.src = URL.createObjectURL(file)
  }, [onSelect, currentColor])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [onClose])

  return (
    <div className="emoji-picker" ref={ref} data-testid={EMOJI_PICKER_TESTIDS.popover}>
      <InlineNotice
        message={uploadError}
        onClose={() => setUploadError(null)}
        kind="error"
        testId={EMOJI_PICKER_TESTIDS.uploadTooLargeNotice}
      />
      <div className="emoji-picker__colors">
        {COLOR_PRESETS.map(c => (
          <button
            key={c}
            type="button"
            className={`emoji-picker__color${c === currentColor ? ' emoji-picker__color--active' : ''}`}
            style={{ background: c }}
            onClick={() => onSelect(value || '', c)}
            data-testid={EMOJI_PICKER_TESTIDS.colorSwatch}
            data-color={c}
          />
        ))}
      </div>
      <div className="emoji-picker__grid">
        {EMOJI_LIST.map(e => (
          <button
            key={e}
            type="button"
            className={`emoji-picker__emoji${e === value ? ' emoji-picker__emoji--active' : ''}`}
            onClick={() => onSelect(e, currentColor)}
            data-testid={EMOJI_PICKER_TESTIDS.emojiButton}
            data-emoji={e}
          >
            {e}
          </button>
        ))}
      </div>
      <div className="emoji-picker__actions">
        <label className="emoji-picker__upload-btn">
          📁 Загрузить
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            style={{ display: 'none' }}
            onChange={handleUpload}
            data-testid={EMOJI_PICKER_TESTIDS.uploadInput}
          />
        </label>
        {value && (
          <button
            type="button"
            className="emoji-picker__clear"
            onClick={() => onSelect('', currentColor)}
            data-testid={EMOJI_PICKER_TESTIDS.clearBtn}
          >
            ✕ Убрать
          </button>
        )}
      </div>
    </div>
  )
}
